import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Avatar, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { fotosGraph, SCOPES_FOTOS } from '../../api/fotosGraph';
import type { ObtenerToken } from '../../api/fotosGraph';
import { fct } from '../../fct/tokens/tokens';

/** Iniciales de las dos primeras palabras del nombre. */
export const iniciales = (nombre: string): string =>
  nombre.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => Array.from(p)[0].toUpperCase()).join('');

/**
 * Token de Graph para las fotos, pedido aparte de `loginRequest` y solo en silencio (consentimiento incremental).
 * Si MSAL no puede darlo sin pantalla, lanza: nunca hay popup ni redirección por esto.
 */
const useTokenFotos = (): ObtenerToken => {
  const { instance, accounts } = useMsal();
  const ref = useRef({ instance, accounts });
  ref.current = { instance, accounts };
  return useCallback(async () => {
    const { instance: i, accounts: a } = ref.current;
    if (a.length === 0) return null;
    const r = await i.acquireTokenSilent({ scopes: SCOPES_FOTOS, account: a[0] });
    return r.accessToken;
  }, []);
};

/** Pinta cuando la fila entra en pantalla (con un margen), y ya no vuelve a `false`. Sin el observador, siempre. */
const useVisible = (ref: React.RefObject<HTMLElement>): boolean => {
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (visible || !el) return undefined;
    const o = new IntersectionObserver((entradas) => {
      if (entradas.some((e) => e.isIntersecting)) { setVisible(true); o.disconnect(); }
    }, { rootMargin: '120px' });
    o.observe(el);
    return () => o.disconnect();
  }, [ref, visible]);
  return visible;
};

interface FotoProps {
  nombre: string;
  /** Email corporativo: solo viaja a Graph, nunca a la URL de la aplicación ni al almacenamiento. */
  email?: string | null;
  tamano?: number;
}

/** Avatar del empleado: su foto de Entra si hay permiso y foto; si no, sus iniciales sobre el verde tenue de la marca. */
export const FotoEmpleado: React.FC<FotoProps> = ({ nombre, email, tamano = 22 }) => {
  const caja = useRef<HTMLSpanElement>(null);
  const visible = useVisible(caja);
  const obtenerToken = useTokenFotos();
  const [url, setUrl] = useState<string | null>(null);
  // Tras un 429, cuando acaba la pausa, las filas que se quedaron en iniciales lo intentan una vez más.
  const [reintento, setReintento] = useState(0);
  useEffect(() => (url ? undefined : fotosGraph.alReintentar(() => setReintento(1))), [url]);

  useEffect(() => {
    if (!visible || !email) return undefined;
    const control = new AbortController();
    let objeto: string | null = null;
    fotosGraph.pedir(email, obtenerToken, control.signal, tamano > 24 ? 96 : 48).then((blob) => {
      if (control.signal.aborted || !blob) return;
      objeto = URL.createObjectURL(blob);
      setUrl(objeto);
    });
    return () => {
      control.abort();
      if (objeto) URL.revokeObjectURL(objeto);
      setUrl(null);
    };
  }, [visible, email, obtenerToken, tamano, reintento]);

  const fondo = { backgroundColor: fct.marca['verde-tenue'], color: fct.marca['verde-oscuro'], flex: 'none' } as const;
  const sigla = iniciales(nombre);
  return (
    <span
      ref={caja}
      style={{ display: 'inline-flex', lineHeight: 0 }}
      {...(url ? {} : { role: 'img', 'aria-label': nombre })}
    >
      {url ? (
        <Avatar size={tamano} src={url} alt={nombre} style={fondo} />
      ) : (
        <Avatar size={tamano} style={fondo} icon={sigla ? undefined : <UserOutlined />}>{sigla}</Avatar>
      )}
    </span>
  );
};

/** Aviso discreto, uno solo, mientras Entra no deja ver las fotos: no bloquea nada. */
export const AvisoFotosSinPermiso: React.FC = () => {
  const falta = useSyncExternalStore(fotosGraph.suscribir, fotosGraph.sinPermiso);
  if (!falta) return null;
  return (
    <Typography.Text type="secondary" role="status" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
      Las fotos necesitan un permiso de Entra
    </Typography.Text>
  );
};
